import { useEffect, useRef, useState } from "react";
import { useContent } from "../contexts/ContentContext";

function useInView(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setSeen(true),
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return seen;
}

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const seen = useInView(ref);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const duration = 1400;
    const t0 = performance.now();
    let frame;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [seen, to]);
  return (
    <span ref={ref} className="font-display text-5xl lg:text-6xl font-medium tracking-tight">
      {Number(val).toLocaleString()}{suffix}
    </span>
  );
}

export default function Achievements() {
  const { content } = useContent();
  const a = content?.achievements || {};
  if (a.enabled === false) return null;
  const stats = a.stats || [];

  return (
    <section id="achievements" className="py-20 lg:py-28 bg-primary text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 grain-bg" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <div className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>{a.eyebrow}</div>
          <h2 className="font-display mt-3 text-3xl lg:text-4xl font-medium tracking-tight">
            {a.heading}
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}>
              <Counter to={Number(s.value) || 0} suffix={s.suffix || ''} />
              <div className="mt-3 text-sm uppercase tracking-widest text-white/80">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
